import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { ALL_DEPTS, PERSONAS, slotOf, type PersonaDef } from "../data/org";

/** Storage que detecta quota e auto-compacta o RH (avatares grandes, reviews antigas, system prompts). */
const safeRHStorage = createJSONStorage(() => ({
  getItem: (name: string) => localStorage.getItem(name),
  setItem: (name: string, value: string) => {
    try { localStorage.setItem(name, value); }
    catch (e: any) {
      if (e?.name !== "QuotaExceededError" && !/quota/i.test(String(e?.message))) throw e;
      try {
        const parsed = JSON.parse(value);
        if (!parsed?.state?.employees) throw e;
        // Compacta: remove avatares grandes, limita reviews, trunca system prompts
        const emps: Record<string, any> = parsed.state.employees;
        let saved = 0;
        for (const id in emps) {
          const e = emps[id];
          if (e.avatarUrl?.startsWith("data:") && e.avatarUrl.length > 80_000) {
            saved += e.avatarUrl.length;
            delete e.avatarUrl;  // remove avatar grande
          }
          if (Array.isArray(e.reviews) && e.reviews.length > 3) {
            const before = JSON.stringify(e.reviews).length;
            e.reviews = e.reviews.slice(0, 3);  // mantém só as 3 últimas
            saved += before - JSON.stringify(e.reviews).length;
          }
          if (typeof e.systemPromptOverride === "string" && e.systemPromptOverride.length > 3000) {
            saved += e.systemPromptOverride.length - 3000;
            e.systemPromptOverride = e.systemPromptOverride.slice(0, 3000) + "\n…[truncado]";
          }
          if (typeof e.notes === "string" && e.notes.length > 500) {
            e.notes = e.notes.slice(0, 500);
          }
        }
        // Limita events a 50
        if (Array.isArray(parsed.state.events) && parsed.state.events.length > 50) {
          parsed.state.events = parsed.state.events.slice(0, 50);
        }
        const compact = JSON.stringify(parsed);
        localStorage.setItem(name, compact);
        if (typeof window !== "undefined") {
          setTimeout(() => alert(
            `⚠ RH com localStorage cheio. Auto-compactei (avatares grandes removidos, ${saved > 0 ? Math.round(saved/1024) + "KB poupados" : "reviews/notas limitadas"}).\n\n` +
            "Pra não perder dados, ligue Cloud Sync em ⚙️ → ↑ ENVIAR."
          ), 100);
        }
      } catch {
        try { localStorage.removeItem(name); } catch {}
      }
    }
  },
  removeItem: (name: string) => localStorage.removeItem(name)
}));

export type Status = "hired" | "available" | "fired";
export type EventKind = "hire" | "fire" | "rehire" | "salary" | "note" | "reset";

export interface Review {
  id: string;
  rating: number;          // 1-5
  managerNote: string;
  text: string;            // texto gerado pela IA (ou manual)
  at: number;
}

export interface Employee {
  personaId: string;
  status: Status;
  hiredAt?: number;
  firedAt?: number;
  salary?: number;
  notes?: string;
  fireReason?: string;
  reviews?: Review[];
  /** data URL ou URL externa do retrato gerado por IA */
  avatarUrl?: string;
  /** se setado, sobrescreve persona.slotId — usado pra drag&drop entre vagas */
  slotOverride?: string;
  /** system prompt customizado — usado em autoExecute, substitui o default do profile */
  systemPromptOverride?: string;
}

export interface LogEvent {
  id: string;
  kind: EventKind;
  personaId?: string;
  at: number;
  detail?: string;
}

interface RHState {
  employees: Record<string, Employee>;
  events: LogEvent[];
  hire:   (personaId: string) => string | null;
  fire:   (personaId: string, reason?: string) => void;
  rehire: (personaId: string) => string | null;
  setSalary: (personaId: string, salary: number) => void;
  setNote:   (personaId: string, note: string) => void;
  addReview: (personaId: string, review: Omit<Review, "id" | "at">) => void;
  removeReview: (personaId: string, reviewId: string) => void;
  setAvatar: (personaId: string, avatarUrl: string) => void;
  setSystemPrompt: (personaId: string, prompt: string) => void;
  moveAgent: (personaId: string, slotId: string) => string | null;
  resetSlot: (personaId: string) => void;
  /** effectiveSlot considera override (ou cai pro slotId nativo da persona) */
  effectiveSlot: (personaId: string) => string;
  reset:  () => void;
  hiredAtSlot: (slotId: string) => string[];
  effectiveSalary: (personaId: string) => number;
  totalCost: () => number;
  totalPower: () => number;
  totalHired: () => number;
}

const initialMap = (): Record<string, Employee> => {
  const out: Record<string, Employee> = {};
  PERSONAS.forEach(p => { out[p.id] = { personaId: p.id, status: "available" }; });
  return out;
};
const findPersona = (id: string): PersonaDef | undefined => PERSONAS.find(p => p.id === id);
const slotCapacity = (slotId: string): number => {
  const [d, r] = slotId.split(":");
  const dept = ALL_DEPTS.find(x => x.id === d);
  return dept?.roles.find(x => x.id === r)?.capacity ?? 0;
};
const uid = () => Math.random().toString(36).slice(2, 10);

export const useRH = create<RHState>()(
  persist(
    (set, get) => ({
      employees: initialMap(),
      events: [],

      hire: (personaId) => {
        const p = findPersona(personaId);
        if (!p) return "Persona não encontrada.";
        const cap = slotCapacity(p.slotId);
        if (get().hiredAtSlot(p.slotId).length >= cap)
          return `Vaga lotada — capacidade ${cap}.`;
        set(s => ({
          employees: {
            ...s.employees,
            [personaId]: { ...s.employees[personaId], personaId, status: "hired", hiredAt: Date.now(), fireReason: undefined }
          },
          events: [{ id: uid(), kind: "hire", personaId, at: Date.now(), detail: p.name }, ...s.events].slice(0, 200)
        }));
        return null;
      },

      fire: (personaId, reason) => set(s => {
        const p = findPersona(personaId);
        return {
          employees: {
            ...s.employees,
            [personaId]: { ...s.employees[personaId], status: "fired", firedAt: Date.now(), fireReason: reason }
          },
          events: [{
            id: uid(), kind: "fire", personaId, at: Date.now(),
            detail: `${p?.name}${reason ? " · " + reason : ""}`
          }, ...s.events].slice(0, 200)
        };
      }),

      rehire: (personaId) => {
        const p = findPersona(personaId);
        if (!p) return "Persona não encontrada.";
        const cap = slotCapacity(p.slotId);
        if (get().hiredAtSlot(p.slotId).length >= cap)
          return `Vaga lotada — capacidade ${cap}.`;
        set(s => ({
          employees: {
            ...s.employees,
            [personaId]: { ...s.employees[personaId], status: "hired", hiredAt: Date.now(), fireReason: undefined }
          },
          events: [{ id: uid(), kind: "rehire", personaId, at: Date.now(), detail: p.name }, ...s.events].slice(0, 200)
        }));
        return null;
      },

      setSalary: (personaId, salary) => set(s => {
        const p = findPersona(personaId);
        return {
          employees: { ...s.employees, [personaId]: { ...s.employees[personaId], salary } },
          events: [{
            id: uid(), kind: "salary", personaId, at: Date.now(),
            detail: `${p?.name} → USD ${salary.toLocaleString("en-US")}`
          }, ...s.events].slice(0, 200)
        };
      }),

      setNote: (personaId, note) => set(s => ({
        employees: { ...s.employees, [personaId]: { ...s.employees[personaId], notes: note } }
      })),

      addReview: (personaId, review) => set(s => {
        const emp = s.employees[personaId];
        const p = findPersona(personaId);
        const newReview: Review = { ...review, id: uid(), at: Date.now() };
        return {
          employees: {
            ...s.employees,
            [personaId]: { ...emp, reviews: [newReview, ...(emp?.reviews ?? [])] }
          },
          events: [{
            id: uid(), kind: "note", personaId, at: Date.now(),
            detail: `Review ${review.rating}/5 — ${p?.name}`
          }, ...s.events].slice(0, 200)
        };
      }),

      removeReview: (personaId, reviewId) => set(s => {
        const emp = s.employees[personaId];
        return {
          employees: {
            ...s.employees,
            [personaId]: { ...emp, reviews: (emp?.reviews ?? []).filter(r => r.id !== reviewId) }
          }
        };
      }),

      setAvatar: (personaId, avatarUrl) => set(s => ({
        employees: {
          ...s.employees,
          [personaId]: { ...s.employees[personaId], personaId, avatarUrl }
        }
      })),

      setSystemPrompt: (personaId, prompt) => set(s => ({
        employees: {
          ...s.employees,
          [personaId]: { ...s.employees[personaId], personaId, systemPromptOverride: prompt || undefined }
        }
      })),

      effectiveSlot: (personaId) => {
        const emp = get().employees[personaId];
        return emp?.slotOverride ?? findPersona(personaId)?.slotId ?? "";
      },

      moveAgent: (personaId, slotId) => {
        const p = findPersona(personaId);
        if (!p) return "persona não encontrada";
        // não checamos capacity pra slots customizados — apenas slots fixos
        set(s => ({
          employees: {
            ...s.employees,
            [personaId]: { ...s.employees[personaId], personaId, slotOverride: slotId }
          },
          events: [{
            id: uid(), kind: "note", personaId, at: Date.now(),
            detail: `${p.name} movido → ${slotId}`
          }, ...s.events].slice(0, 200)
        }));
        return null;
      },

      resetSlot: (personaId) => set(s => {
        const emp = s.employees[personaId];
        if (!emp) return s;
        const { slotOverride, ...rest } = emp;
        return { employees: { ...s.employees, [personaId]: rest } };
      }),

      reset: () => set({
        employees: initialMap(),
        events: [{ id: uid(), kind: "reset", at: Date.now(), detail: "Reset total" }]
      }),

      hiredAtSlot: (slotId) =>
        Object.values(get().employees)
          .filter(e => e.status === "hired")
          .filter(e => (e.slotOverride ?? findPersona(e.personaId)?.slotId) === slotId)
          .map(e => e.personaId),

      effectiveSalary: (personaId) => {
        const emp = get().employees[personaId];
        if (emp?.salary != null) return emp.salary;
        return findPersona(personaId)?.cost ?? 0;
      },

      totalCost: () => {
        const st = get();
        return Object.values(st.employees)
          .filter(e => e.status === "hired")
          .reduce((acc, e) => acc + st.effectiveSalary(e.personaId), 0);
      },

      totalPower: () =>
        Object.values(get().employees)
          .filter(e => e.status === "hired")
          .reduce((acc, e) => acc + (findPersona(e.personaId)?.power ?? 0), 0),

      totalHired: () =>
        Object.values(get().employees).filter(e => e.status === "hired").length
    }),
    { name: "agencia-vpc-rh-v2", storage: safeRHStorage }
  )
);

export { slotOf };
